import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Tabs, 
  Tab, 
  Card, 
  CardContent,
  Button,
  Dialog,
  IconButton,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Add as AddIcon, 
  Close as CloseIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  LocalHospital as HospitalIcon,
  Person as DoctorIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, ZoomControl, ScaleControl, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Interface for cohort type
interface Cohort {
  id: number;
  name: string;
  description: string;
  type: 'user' | 'iris';
  category?: string;
  patientCount: number;
  revenueValue: number;
  criteria: string[];
  locations: { lat: number; lng: number; count: number }[];
  topFacilities: { id: number; name: string; patientCount: number }[];
  topProviders: { id: number; name: string; patientCount: number }[];
  careScore?: {
    overall: number;
    quality: number;
    access: number;
    opportunities: Array<{
      id: number;
      title: string;
      description: string;
      impact: 'high' | 'medium' | 'low';
      scoreImprovement: number;
    }>;
  };
}

// Sample data for demonstration
const sampleCohorts: Cohort[] = [
  {
    id: 1,
    name: "Diabetes Type 2 Patients",
    description: "Patients with Type 2 Diabetes requiring routine care",
    type: "user",
    patientCount: 3245,
    revenueValue: 1234567,
    criteria: ["Diagnosis: Type 2 Diabetes", "Age > 45", "BMI > 25"],
    locations: [
      { lat: 28.5223, lng: -81.3774, count: 120 },
      { lat: 28.5758, lng: -81.3656, count: 85 },
      { lat: 28.5947, lng: -81.3543, count: 65 }
    ],
    topFacilities: [
      { id: 1, name: "Orlando Regional Medical Center", patientCount: 120 },
      { id: 2, name: "AdventHealth Orlando", patientCount: 85 }
    ],
    topProviders: [
      { id: 1, name: "Dr. Jane Smith", patientCount: 42 },
      { id: 2, name: "Dr. Robert Johnson", patientCount: 38 }
    ],
    careScore: {
      overall: 76,
      quality: 79,
      access: 72,
      opportunities: [
        {
          id: 1,
          title: "Improve Diabetes Care Protocols",
          description: "Implement standardized protocols for diabetes management",
          impact: "high",
          scoreImprovement: 5
        },
        {
          id: 2,
          title: "Enhance Telehealth for Diabetes Monitoring",
          description: "Expand telehealth services for remote diabetes monitoring",
          impact: "medium",
          scoreImprovement: 3
        }
      ]
    }
  },
  {
    id: 2,
    name: "Cardiac Care Patients",
    description: "Patients requiring cardiac care and monitoring",
    type: "user",
    patientCount: 1876,
    revenueValue: 987654,
    criteria: ["Diagnosis: Hypertension", "Diagnosis: Coronary Artery Disease", "Age > 55"],
    locations: [
      { lat: 28.5383, lng: -81.3792, count: 95 },
      { lat: 28.6024, lng: -81.2001, count: 75 }
    ],
    topFacilities: [
      { id: 1, name: "Orlando Regional Medical Center", patientCount: 95 },
      { id: 3, name: "Winter Park Memorial Hospital", patientCount: 75 }
    ],
    topProviders: [
      { id: 3, name: "Dr. Lisa Wong", patientCount: 32 },
      { id: 4, name: "Dr. Michael Brown", patientCount: 28 }
    ],
    careScore: {
      overall: 83,
      quality: 85,
      access: 80,
      opportunities: [
        {
          id: 1,
          title: "Optimize Cardiac Care Coordination",
          description: "Improve care coordination between specialists and primary care",
          impact: "medium",
          scoreImprovement: 3
        }
      ]
    }
  },
  {
    id: 3,
    name: "High Revenue Medicare Patients",
    description: "Medicare patients with high reimbursement rates",
    type: "iris",
    category: "revenue",
    patientCount: 2543,
    revenueValue: 2345678,
    criteria: ["Insurance: Medicare", "Age > 65", "Multiple chronic conditions"],
    locations: [
      { lat: 28.5223, lng: -81.3774, count: 110 },
      { lat: 28.5758, lng: -81.3656, count: 90 }
    ],
    topFacilities: [
      { id: 1, name: "Orlando Regional Medical Center", patientCount: 110 },
      { id: 2, name: "AdventHealth Orlando", patientCount: 90 }
    ],
    topProviders: [
      { id: 1, name: "Dr. Jane Smith", patientCount: 45 },
      { id: 3, name: "Dr. Lisa Wong", patientCount: 38 }
    ],
    careScore: {
      overall: 72,
      quality: 75,
      access: 68,
      opportunities: [
        {
          id: 1,
          title: "Improve Medicare Patient Access",
          description: "Optimize scheduling for Medicare patients to reduce wait times",
          impact: "high",
          scoreImprovement: 4
        },
        {
          id: 2,
          title: "Enhance Care Coordination",
          description: "Implement dedicated care coordinators for complex Medicare patients",
          impact: "high",
          scoreImprovement: 5
        }
      ]
    }
  },
  {
    id: 4,
    name: "Underserved Seniors",
    description: "Market opportunity: seniors in areas with limited medical services",
    type: "iris",
    category: "opportunity",
    patientCount: 3156,
    revenueValue: 3456789,
    criteria: ["Age > 70", "Geographic: East Orlando", "Limited provider access"],
    locations: [
      { lat: 28.6024, lng: -81.2001, count: 180 },
      { lat: 28.5947, lng: -81.3543, count: 120 }
    ],
    topFacilities: [
      { id: 3, name: "Winter Park Memorial Hospital", patientCount: 120 },
      { id: 2, name: "AdventHealth Orlando", patientCount: 85 }
    ],
    topProviders: [
      { id: 4, name: "Dr. Michael Brown", patientCount: 42 },
      { id: 2, name: "Dr. Robert Johnson", patientCount: 36 }
    ],
    careScore: {
      overall: 65,
      quality: 68,
      access: 61,
      opportunities: [
        {
          id: 1,
          title: "Increase Access Points",
          description: "Establish new clinics in underserved areas with high senior populations",
          impact: "high",
          scoreImprovement: 7
        },
        {
          id: 2,
          title: "Implement Mobile Services",
          description: "Create mobile health services for seniors with limited transportation",
          impact: "high",
          scoreImprovement: 6
        },
        {
          id: 3,
          title: "Expand Telehealth",
          description: "Expand telehealth services with senior-friendly interfaces",
          impact: "medium",
          scoreImprovement: 4
        }
      ]
    }
  }
];

const PatientCohorts: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [cohortDetailOpen, setCohortDetailOpen] = useState(false);
  const [showMapInBuilder, setShowMapInBuilder] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  
  // Filter cohorts based on tab selection
  const userCohorts = sampleCohorts.filter(cohort => cohort.type === 'user');
  const irisCohorts = sampleCohorts.filter(cohort => cohort.type === 'iris');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCohortClick = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    setCohortDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setCohortDetailOpen(false);
  };

  const handleAddToProject = () => {
    setProjectDialogOpen(true);
  };

  const handleCloseProjectDialog = () => {
    setProjectDialogOpen(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Helper function to get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 85) return '#4caf50';
    if (score >= 70) return '#ff9800';
    return '#f44336';
  };
  
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'low': return '#f44336';
      default: return '#757575';
    }
  };

  // Component for rendering a cohort card
  const CohortCard = ({ cohort }: { cohort: Cohort }) => (
    <Card 
      sx={{ 
        cursor: 'pointer', 
        transition: 'transform 0.2s', 
        '&:hover': { transform: 'scale(1.02)' } 
      }}
      onClick={() => handleCohortClick(cohort)}
    >
      <CardContent>
        <Typography variant="h6" component="div" gutterBottom>
          {cohort.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {cohort.description}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">{cohort.patientCount.toLocaleString()} patients</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MoneyIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">{formatCurrency(cohort.revenueValue)}</Typography>
          </Box>
        </Box>
        {cohort.type === 'iris' && (
          <Chip 
            label={cohort.category === 'revenue' ? 'Revenue Leader' : 'Market Opportunity'} 
            size="small" 
            color={cohort.category === 'revenue' ? 'success' : 'primary'}
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>
    </Card>
  );

  // Cohort Creator Component
  const CohortCreator = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Create a New Patient Cohort
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />}>
          Create with GPT Assistant
        </Button>
        <Button variant="outlined">
          Build with Filters
        </Button>
        <Button 
          variant="outlined" 
          color={showMapInBuilder ? "primary" : "inherit"}
          onClick={() => setShowMapInBuilder(!showMapInBuilder)}
        >
          {showMapInBuilder ? "Hide Map" : "Show Map"}
        </Button>
      </Box>
      
      {showMapInBuilder && (
        <Box sx={{ height: 400, mt: 2 }}>
          <MapContainer 
            center={[28.5383, -81.3792]} 
            zoom={10}
            scrollWheelZoom={true}
            zoomControl={false}
            style={{ height: '100%', width: '100%' }}
          >
            <ZoomControl position="topright" />
            <ScaleControl position="bottomright" />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </MapContainer>
        </Box>
      )}
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="body1">
          Use the cohort builder to define patient groups based on:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <Chip label="Demographics/SDOH" />
          <Chip label="Geographic Location" />
          <Chip label="EHR Data" />
          <Chip label="Claims Data" />
          <Chip label="Financial Metrics" />
          <Chip label="Insurance Coverage" />
        </Box>
      </Box>
    </Box>
  );

  // Component for the care score section in the cohort detail dialog
  const CareScoreSection: React.FC<{ cohort: Cohort }> = ({ cohort }) => {
    if (!cohort.careScore) return null;
    
    return (
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Typography variant="h6">Care Score Analysis</Typography>
          <Divider sx={{ my: 1 }} />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Overall Care Score</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: getScoreColor(cohort.careScore.overall)
                  }}
                >
                  {cohort.careScore.overall}
                </Typography>
                <Typography variant="body1" sx={{ ml: 0.5 }}>/100</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Score Breakdown</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ width: '48%' }}>
                  <Typography variant="body2" gutterBottom>Quality of Care</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: '80%', 
                      bgcolor: '#f5f5f5', 
                      height: 10, 
                      borderRadius: 5,
                      mr: 1
                    }}>
                      <Box sx={{ 
                        width: `${cohort.careScore.quality}%`, 
                        bgcolor: getScoreColor(cohort.careScore.quality), 
                        height: 10, 
                        borderRadius: 5 
                      }} />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: getScoreColor(cohort.careScore.quality)
                      }}
                    >
                      {cohort.careScore.quality}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ width: '48%' }}>
                  <Typography variant="body2" gutterBottom>Access to Care</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: '80%', 
                      bgcolor: '#f5f5f5', 
                      height: 10, 
                      borderRadius: 5,
                      mr: 1
                    }}>
                      <Box sx={{ 
                        width: `${cohort.careScore.access}%`, 
                        bgcolor: getScoreColor(cohort.careScore.access), 
                        height: 10, 
                        borderRadius: 5 
                      }} />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: getScoreColor(cohort.careScore.access)
                      }}
                    >
                      {cohort.careScore.access}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {cohort.careScore.opportunities && cohort.careScore.opportunities.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Care Score Optimization Opportunities
                </Typography>
                <List>
                  {cohort.careScore.opportunities.map((opportunity, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        borderLeft: `4px solid ${getImpactColor(opportunity.impact)}`,
                        pl: 2,
                        mb: 1
                      }}
                    >
                      <ListItemIcon>
                        <LightbulbIcon sx={{ color: getImpactColor(opportunity.impact) }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={opportunity.title}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="body2">{opportunity.description}</Typography>
                          </Box>
                        }
                      />
                      <Chip 
                        size="small" 
                        icon={<TrendingUpIcon />}
                        label={`+${opportunity.scoreImprovement}`} 
                        sx={{ 
                          bgcolor: getImpactColor(opportunity.impact), 
                          color: 'white' 
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  // Cohort Detail Dialog
  const CohortDetailDialog = () => (
    <Dialog
      open={cohortDetailOpen}
      onClose={handleCloseDetail}
      maxWidth="lg"
      fullWidth
    >
      {selectedCohort && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
            <Typography variant="h5">{selectedCohort.name}</Typography>
            <Box>
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />} 
                onClick={handleAddToProject}
                sx={{ mr: 1 }}
              >
                Add to Project
              </Button>
              <IconButton onClick={handleCloseDetail}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>{selectedCohort.description}</Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Patient Population</Typography>
                    <Typography variant="h4">{selectedCohort.patientCount.toLocaleString()}</Typography>
                    <Typography variant="body2" color="text.secondary">Unique patient lives in your market</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Revenue Value</Typography>
                    <Typography variant="h4">{formatCurrency(selectedCohort.revenueValue)}</Typography>
                    <Typography variant="body2" color="text.secondary">Estimated annual value</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Cohort Type</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {selectedCohort.type === 'iris' ? (
                        <Chip 
                          label={selectedCohort.category === 'revenue' ? "Revenue Opportunity" : "Market Opportunity"} 
                          color="primary"
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <Chip 
                          label="User Defined" 
                          color="secondary"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <CareScoreSection cohort={selectedCohort} />
            
            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6">Top Facilities</Typography>
                <Divider sx={{ mb: 2, mt: 1 }} />
                {selectedCohort.topFacilities.map((facility, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <HospitalIcon sx={{ mr: 1 }} />
                        <Typography variant="body1">{facility.name}</Typography>
                      </Box>
                      <Typography variant="body2">{facility.patientCount} patients</Typography>
                    </CardContent>
                  </Card>
                ))}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6">Top Providers</Typography>
                <Divider sx={{ mb: 2, mt: 1 }} />
                {selectedCohort.topProviders.map((provider, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DoctorIcon sx={{ mr: 1 }} />
                        <Typography variant="body1">{provider.name}</Typography>
                      </Box>
                      <Typography variant="body2">{provider.patientCount} patients</Typography>
                    </CardContent>
                  </Card>
                ))}
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Geographic Distribution</Typography>
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Box sx={{ height: 400, width: '100%', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                <MapContainer 
                  center={[28.5383, -81.3792]} 
                  zoom={10} 
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <ZoomControl position="bottomright" />
                  <ScaleControl position="bottomleft" />
                  
                  {selectedCohort.locations.map((location, index) => (
                    <CircleMarker 
                      key={index}
                      center={[location.lat, location.lng]}
                      radius={Math.sqrt(location.count) / 2 + 5}
                      fillColor="#3f51b5"
                      color="#3f51b5"
                      weight={1}
                      opacity={0.8}
                      fillOpacity={0.5}
                    >
                      <Popup>
                        {location.count} patients in this area
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </Box>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Selection Criteria</Typography>
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedCohort.criteria.map((criterion, index) => (
                  <Chip key={index} label={criterion} />
                ))}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  );

  // Add to Project Dialog
  const AddToProjectDialog = () => (
    <Dialog
      open={projectDialogOpen}
      onClose={handleCloseProjectDialog}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Add to Project</Typography>
        <Typography variant="body1" paragraph>
          Select an existing project or create a new one to add this cohort.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Button variant="outlined">Create New Project</Button>
          <Button variant="outlined">Add to Existing Project</Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button onClick={handleCloseProjectDialog}>Cancel</Button>
        </Box>
      </Box>
    </Dialog>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Patient Cohorts
      </Typography>
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="My Cohorts" />
        <Tab label="Iris Recommendations" />
        <Tab label="Create Cohort" />
      </Tabs>
      
      {/* My Cohorts Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {userCohorts.map(cohort => (
            <Grid item xs={12} sm={6} md={4} key={cohort.id}>
              <CohortCard cohort={cohort} />
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Iris Recommendations Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {irisCohorts.map(cohort => (
            <Grid item xs={12} sm={6} md={4} key={cohort.id}>
              <CohortCard cohort={cohort} />
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Create Cohort Tab */}
      {tabValue === 2 && <CohortCreator />}
      
      {/* Cohort Detail Dialog */}
      <CohortDetailDialog />
      
      {/* Add to Project Dialog */}
      <AddToProjectDialog />
    </Box>
  );
};

export default PatientCohorts; 